import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Icon } from './Icon';

// ---------------------------------------------------------------------------
// Voice notes: record with the microphone and turn speech into text while speaking (the browser's own
// speech recognition: Chrome, Edge, Safari; Arabic or English). The recording is uploaded to the record's
// files and the activity's text ends with a [voice:<file id>] marker, which the timeline turns into a player.
// ---------------------------------------------------------------------------

/* The Web Speech API isn't in TypeScript's DOM types: the parts used here. */
interface SpeechResult { isFinal: boolean; 0: { transcript: string } }
interface SpeechEvent { resultIndex: number; results: ArrayLike<SpeechResult> }
interface Recognition {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((e: SpeechEvent) => void) | null; onerror: ((e: { error: string }) => void) | null; onend: (() => void) | null;
  start(): void; stop(): void;
}
type RecognitionCtor = new () => Recognition;
const speechCtor = (): RecognitionCtor | null => {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};
export const speechSupported = () => typeof window !== 'undefined' && !!speechCtor();
const recordingSupported = () => typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

export type VoiceLang = 'ar-SA' | 'en-US';
const LANG_KEY = 'crm.voiceLang';
const savedLang = (): VoiceLang => { try { return localStorage.getItem(LANG_KEY) === 'en-US' ? 'en-US' : 'ar-SA'; } catch { return 'ar-SA'; } };

const VOICE_MARK = /\n*\[voice:([0-9a-f-]{36})\]\s*$/i;
/** Splits an activity's text into the words and the recording's file id (if any). */
export const splitVoice = (description: string | null | undefined) => {
  const m = VOICE_MARK.exec(description ?? '');
  return { text: (description ?? '').replace(VOICE_MARK, ''), fileId: m?.[1] ?? null };
};
export const withVoice = (text: string, fileId: string | null) => (fileId ? `${text.trim()}\n\n[voice:${fileId}]` : text.trim());

/**
 * Speech to text into a text box: each final phrase is appended to `value` through `onChange`.
 * Returns the live (not yet final) words for display.
 */
function useDictation(onText: (phrase: string) => void) {
  const rec = useRef<Recognition | null>(null);
  const wanted = useRef(false);
  const [live, setLive] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [on, setOn] = useState(false);
  const start = (lang: VoiceLang) => {
    const Ctor = speechCtor();
    if (!Ctor) { setError('Speech-to-text isn’t available in this browser. Use Chrome, Edge or Safari.'); return; }
    const r = new Ctor();
    r.lang = lang; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) onText(res[0].transcript.trim()); else interim += res[0].transcript;
      }
      setLive(interim);
    };
    r.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      setError(e.error === 'not-allowed' ? 'Allow the microphone for this site to use speech-to-text.' : `Speech-to-text stopped (${e.error}). The recording continues; you can type the rest.`);
      wanted.current = false;
    };
    // Browsers stop listening after a pause: carry on while the person is still recording.
    r.onend = () => { setLive(''); if (wanted.current) { try { r.start(); } catch { wanted.current = false; setOn(false); } } else setOn(false); };
    rec.current = r; wanted.current = true; setError(null); setOn(true);
    try { r.start(); } catch { setError('Speech-to-text couldn’t start.'); wanted.current = false; setOn(false); }
  };
  const stop = () => { wanted.current = false; rec.current?.stop(); setOn(false); setLive(''); };
  useEffect(() => () => { wanted.current = false; rec.current?.stop(); }, []);
  return { start, stop, live, error, on };
}

/** A small "Dictate" button for any text box: speak, and the words are added to the box. */
export function DictateButton({ onText }: { onText: (phrase: string) => void }) {
  const d = useDictation(onText);
  const [lang, setLang] = useState<VoiceLang>(savedLang);
  if (!speechSupported()) return null;
  return (
    <span className="dictate">
      <select aria-label="Dictation language" value={lang} onChange={(e) => { const l = e.target.value as VoiceLang; setLang(l); try { localStorage.setItem(LANG_KEY, l); } catch { /* private mode */ } }} disabled={d.on}>
        <option value="ar-SA">عربي</option><option value="en-US">English</option>
      </select>
      <button type="button" className={`btn small dictate-btn${d.on ? ' rec' : ''}`} onClick={() => (d.on ? d.stop() : d.start(lang))}>{d.on ? '■ Stop' : 'Dictate'}</button>
      {d.live && <span className="small muted dictate-live">{d.live}</span>}
      {d.error && <span className="small form-error">{d.error}</span>}
    </span>
  );
}

/**
 * The voice note recorder: records audio and writes the text live; the finished recording goes to `onAudio`.
 */
/** A live sound wave from the microphone while recording (on this device only; nothing is sent anywhere). */
function VoiceWave({ stream }: { stream: MediaStream }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    const c = canvas.current;
    if (!AC || !c) return;
    const ctx = new AC();
    // Started after the microphone question: the browser may have created it paused.
    void ctx.resume().catch(() => { /* stays flat; recording still works */ });
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 64;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const g = c.getContext('2d');
    const color = getComputedStyle(c).color || '#dc2626';
    let raf = 0;
    const draw = () => {
      if (!g) return;
      analyser.getByteFrequencyData(data);
      g.clearRect(0, 0, c.width, c.height);
      const bars = 24, w = c.width / bars;
      g.fillStyle = color;
      for (let i = 0; i < bars; i++) {
        const v = (data[i + 1] ?? 0) / 255;
        const h = Math.max(3, v * c.height);
        g.fillRect(i * w + 1, (c.height - h) / 2, w - 2, h);
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); void ctx.close(); };
  }, [stream]);
  return <canvas ref={canvas} className="voice-wave" width={120} height={28} aria-hidden="true" />;
}

export function VoiceRecorder({ text, setText, onAudio }: {
  text: string; setText: (fn: (t: string) => string) => void; onAudio: (blob: Blob | null) => void;
}) {
  const [lang, setLang] = useState<VoiceLang>(savedLang);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const media = useRef<MediaRecorder | null>(null);
  // The microphone while recording: drives the live sound wave.
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const d = useDictation((p) => setText((t) => (t ? `${t} ${p}` : p)));

  const start = async () => {
    setMicError(null); setBlob(null); setAudioUrl(null); onAudio(null); chunks.current = [];
    if (recordingSupported()) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream);
        mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
        mr.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const b = new Blob(chunks.current, { type: mr.mimeType || 'audio/webm' });
          setBlob(b); setAudioUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b); }); onAudio(b);
        };
        mr.start(1000);
        media.current = mr;
        setLiveStream(stream);
      } catch {
        setMicError('Allow the microphone for this site to record.');
        return;
      }
    }
    d.start(lang);
    setRecording(true); setSeconds(0);
    timer.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
  };
  const stop = () => {
    d.stop();
    if (media.current && media.current.state !== 'inactive') media.current.stop();
    media.current = null;
    setLiveStream(null);
    if (timer.current) window.clearInterval(timer.current);
    setRecording(false);
  };
  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); if (media.current && media.current.state !== 'inactive') media.current.stop(); }, []);

  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <div className="voice-rec field wide">
      <div className="voice-row">
        <select aria-label="Language" value={lang} disabled={recording} onChange={(e) => { const l = e.target.value as VoiceLang; setLang(l); try { localStorage.setItem(LANG_KEY, l); } catch { /* private mode */ } }}>
          <option value="ar-SA">عربي (Arabic)</option><option value="en-US">English</option>
        </select>
        {!recording
          ? <button type="button" className="btn primary voice-btn" onClick={() => void start()}><Icon name="mic" />{blob ? 'Record again' : 'Start recording'}</button>
          : <button type="button" className="btn voice-btn rec" onClick={stop}><span className="rec-dot" aria-hidden="true" /> Stop · {mmss}</button>}
        {recording && liveStream && <VoiceWave stream={liveStream} />}
        {audioUrl && !recording && <audio controls src={audioUrl} className="voice-audio" />}
      </div>
      {!speechSupported() && <p className="small muted">This browser can record but not convert speech to text (use Chrome, Edge or Safari for live text). Type a short summary below.</p>}
      {d.live && <p className="small muted voice-live">{d.live}…</p>}
      {(micError || d.error) && <p className="small form-error">{micError ?? d.error}</p>}
      <label className="label" htmlFor="voice-text">Text (written while you speak; you can correct it)</label>
      <textarea id="voice-text" rows={4} maxLength={30000} value={text} onChange={(e) => setText(() => e.target.value)} dir="auto" />
    </div>
  );
}

/** Uploads a recording to the record's files; returns the file id, or null if files are off or it failed. */
export async function uploadRecording(objectName: string, recordId: string, blob: Blob): Promise<string | null> {
  try {
    const cfg = await api<{ enabled: boolean; maxBytes: number }>('/api/files/config');
    if (!cfg.enabled || blob.size > cfg.maxBytes) return null;
    const type = (blob.type || 'audio/webm').split(';')[0]!;
    const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
    const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(':', '-');
    const up = await api<{ fileId: string; uploadUrl: string; headers: Record<string, string> }>('/api/files/uploads', {
      method: 'POST', body: { name: `Voice note ${stamp}.${ext}`, sizeBytes: blob.size, contentType: type, objectName, recordId },
    });
    const put = await fetch(up.uploadUrl, { method: 'PUT', headers: up.headers, body: blob });
    if (!put.ok) return null;
    await api(`/api/files/${up.fileId}/complete`, { method: 'POST' });
    return up.fileId;
  } catch {
    return null;
  }
}

/** Plays a voice note's recording (asks for a short-lived link when pressed). */
export function VoicePlayer({ fileId }: { fileId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setErr(null);
    try { setUrl((await api<{ url: string }>(`/api/files/${fileId}/download`)).url); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not load the recording.'); } finally { setBusy(false); }
  };
  if (url) return <audio controls autoPlay src={url} className="voice-audio" />;
  return (
    <span className="voice-play">
      <button type="button" className="btn small" disabled={busy} onClick={() => void load()}>▶ Play recording</button>
      {err && <span className="small form-error"> {err}</span>}
    </span>
  );
}
